import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, ChevronDown, ChevronRight, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Button from "shared/Button";
import PageHeader from "shared/PageHeader";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";
import { apiErrorMessage } from "./apiError";
import { DUE_GROUPS, conversationsPath, getDueList, getWarmLeads, postTouchAction, type DueList, type TouchView, type WarmLead } from "./api/OutreachTouchesApi";
import { formatCalendarDate } from "./outreachDates";
import TouchCard from "./ui/TouchCard";
import DueStageStrip, { type Arrival } from "./ui/DueStageStrip";
import DueSkeleton from "./ui/DueSkeleton";
import { ALL_STAGES, defaultStage, filterDueList, deadEnds, groupByRfp, stageCount, stageTouches, type StageKey, type StoppedGroup } from "./dueStages";
import StoppedGroupCard from "./ui/StoppedGroupCard";
import DueStatBoxes from "./ui/DueStatBoxes";
import ComingUpModal from "./ui/ComingUpModal";
import LinkedInHubTabs from "./ui/LinkedInHubTabs";
import WarmLeadCard from "./ui/WarmLeadCard";
import StandingHelp from "./ui/StandingHelp";
import { cn } from "lib/utils";
import { useUrlState } from "shared/listNavigation";

const EXIT_MS = 500;
const PAGE_SIZE = 15;      // cards shown before "Show more"

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

const CAUSE_HEADINGS: Record<StoppedGroup["cause"], string> = {
  replied: "Replied",
  skipped: "No LinkedIn profile found",
  other: "Stopped for another reason",
};

function stageOf(data: DueList | null, touchId: number): StageKey | null {
  if (!data) return null;
  return ALL_STAGES.find((s) => stageTouches(s, data).some((t) => t.id === touchId))?.key ?? null;
}

/** The shared LinkedIn due-today page: everyone with CRM access sees the same list. */
export default function OutreachDuePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DueList | null>(null);
  const [error, setError] = useState("");
  const [rfp, setRfp] = useState<number | "">("");
  const [rfps, setRfps] = useState<Record<number, string>>({});
  const [shownRfp, setShownRfp] = useState<number | "">("");     // the RFP filter the list on screen was loaded for
  const [showUpcoming, setShowUpcoming] = useState(false);       // the "coming up" modal
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());     // RFP sections folded shut
  const [selected, setSelected] = useState<Set<number>>(new Set());       // Needs attention: touches ticked for a bulk "profile not found"
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [closedLeads, setClosedLeads] = useState<WarmLead[] | null>(null);     // skipped / not a fit; null = not shown
  // The open stage lives in the URL, so a reload or Back returns to it; blank means "the first stage with work".
  const [stageParam, setStageParam] = useUrlState("stage", "");

  // Touches that were just acted on slide out, then show up in their next stage: the tile there pulses and the row flashes.
  const [leaving, setLeaving] = useState<Set<number>>(new Set());
  const [arrival, setArrival] = useState<Arrival | null>(null);
  const [arrivedId, setArrivedId] = useState<number | null>(null);
  const dataRef = useRef<DueList | null>(null);
  const arrivals = useRef(0);
  const pending = useRef<Set<number>>(new Set());   // acted on, not yet shown in their new stage
  const leaveStartedAt = useRef(0);
  const applyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastGood = useRef<{ d: DueList; rfp: number | "" } | null>(null);   // newest successful response, for when a later reload fails

  const latest = useRef(0);
  const rfpNow = useRef<number | "">(rfp);
  rfpNow.current = rfp;      // an action that finishes after the filter changed must reload for the filter now chosen, not the one it was clicked under
  const load = useCallback((changedId?: number) => {
    const forRfp = rfpNow.current;
    const mine = ++latest.current;      // a slower, older response must never overwrite a newer one
    if (applyTimer.current) clearTimeout(applyTimer.current);   // a newer load replaces one still waiting on the animation
    if (changedId !== undefined) {
      leaveStartedAt.current = Date.now();      // every action gets its own full exit animation
      pending.current.add(changedId);
      setLeaving(new Set(pending.current));
    }
    getDueList({ opportunityId: forRfp === "" ? undefined : forRfp, comingUp: true })
      .then((d) => {
        if (mine !== latest.current) return;
        lastGood.current = { d, rfp: forRfp };
        // keep the rows in place until their exit animation has played
        const wait = pending.current.size === 0 ? 0 : Math.max(0, EXIT_MS - (Date.now() - leaveStartedAt.current));
        applyTimer.current = setTimeout(() => {
          if (mine !== latest.current) return;
          let moved: { id: number; stage: StageKey } | null = null;
          pending.current.forEach((id) => {
            const was = stageOf(dataRef.current, id);
            const now = stageOf(d, id);
            if (was && now && now !== was) moved = { id, stage: now };      // only a row the user saw leave counts as an arrival
          });
          pending.current.clear();
          setLeaving(new Set());
          if (moved) {
            const { id, stage: dest } = moved as { id: number; stage: StageKey };
            setArrival({ stage: dest, n: ++arrivals.current });
            setArrivedId(id);
          }
          dataRef.current = d;
          setData(d);
          setShownRfp(forRfp);
        }, wait);
        setError("");
        // remember every RFP seen so the filter keeps its options while filtered
        setRfps((prev) => {
          const next = { ...prev };
          [...DUE_GROUPS.flatMap((g) => d.groups[g]), ...d.coming_up].forEach((t) => { next[t.opportunity_id] = t.rfp_title ?? `RFP ${t.opportunity_id}`; });
          return next;
        });
      })
      .catch((e) => {
        if (mine !== latest.current) return;
        pending.current.clear();      // never leave a row faded out because its reload failed
        setLeaving(new Set());
        // an earlier reload may have succeeded but been replaced before it was shown: show it rather than a list with actioned cards
        const good = lastGood.current;
        if (good && good.rfp === forRfp && good.d !== dataRef.current) { dataRef.current = good.d; setData(good.d); }
        else if (good?.rfp !== forRfp) { dataRef.current = null; setData(null); }      // the list on screen belongs to another RFP: do not leave it up
        setShownRfp(forRfp);
        setError(apiErrorMessage(e, "Could not load the due list."));
      });
  }, []);

  useEffect(() => () => { if (applyTimer.current) clearTimeout(applyTimer.current); }, []);

  useEffect(() => { load(); }, [load, rfp]);      // the filter changed: load the list for it

  // The highlight and the tile's pulse are one-shot: clear them once they have played.
  useEffect(() => {
    if (!arrival) return;
    const t = setTimeout(() => setArrival(null), 2400);
    return () => clearTimeout(t);
  }, [arrival]);
  useEffect(() => {
    if (arrivedId === null) return;
    const t = setTimeout(() => setArrivedId(null), 4000);
    return () => clearTimeout(t);
  }, [arrivedId]);

  // Conversations moved out of Today into the Inbox: an old link or bookmark to that stage lands there.
  useEffect(() => { if (stageParam === "conversations") navigate(conversationsPath(), { replace: true }); }, [stageParam, navigate]);
  const total = useMemo(() => (data ? data.counts.due_total : 0), [data]);
  const openStage: StageKey = data ? (ALL_STAGES.some((s) => s.key === stageParam) ? (stageParam as StageKey) : defaultStage(data)) : "send";
  const stage = ALL_STAGES.find((s) => s.key === openStage);
  // Pin the open stage before the list reloads: with no stage in the URL the page would otherwise jump to the first stage with work.
  const onTouchChanged = (touchId: number) => { setStageParam(openStage); load(touchId); };
  const loadClosedLeads = useCallback(() => {
    getWarmLeads("closed", rfp === "" ? undefined : rfp).then(setClosedLeads).catch((e) => setError(apiErrorMessage(e, "Could not load the skipped warm leads.")));
  }, [rfp]);
  useEffect(() => { setClosedLeads(null); }, [rfp]);
  const switching = data !== null && shownRfp !== rfp;      // the RFP filter changed and its list has not arrived yet
  const loading = switching || (data === null && !error);
  const isClosed = stage?.key === "closed";
  const isWarm = stage?.key === "warm";
  const view = useMemo(() => (data ? filterDueList(data, query) : null), [data, query]);     // what the search leaves
  const searching = query.trim() !== "";
  const rows = view && stage && !isClosed && !isWarm ? stageTouches(stage, view) : [];
  const stopped = view && stage && isClosed ? deadEnds(stageTouches(stage, view)) : [];
  const warmLeads = view?.warm_leads ?? [];
  const shown = view && stage ? stageCount(stage, view) : 0;
  const sections = groupByRfp(rows);
  const isNeeds = stage?.key === "needs";
  const selectable = rows.filter((t) => t.needs_profile && t.actions.includes("no_profile"));
  useEffect(() => { setLimit(PAGE_SIZE); setSelected(new Set()); }, [openStage, query, rfp]);      // a new view starts at the top, with nothing ticked
  const toggleSection = (id: number) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSelected = (id: number) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function markSelectedNoProfile() {
    const reason = bulkReason.trim();
    if (!reason || selected.size === 0) return;
    setBulkBusy(true);
    setError("");
    let failed = 0;
    for (const t of selectable.filter((x) => selected.has(x.id))) {      // one at a time: each takes the company's lock
      try { await postTouchAction(t.opportunity_id, t.id, { action: "no_profile", reason }); } catch { failed += 1; }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setBulkReason("");
    setStageParam(openStage);
    if (failed > 0) setError(`${failed} of the selected could not be skipped (reload and try again).`);
    load();
  }

  /** Hand out the "show more" budget to sections in order; a folded section costs nothing and an exhausted one is left out. */
  function allot<T>(secs: { key: string | number; items: T[] }[]) {
    let budget = limit;
    const out = secs.map((sec) => {
      if (collapsed.has(sec.key as number)) return { sec, take: [] as T[], folded: true };
      const take = sec.items.slice(0, Math.max(0, budget));
      budget -= take.length;
      return { sec, take, folded: false };
    });
    const open = secs.filter((s2) => !collapsed.has(s2.key as number)).reduce((n, s2) => n + s2.items.length, 0);
    return { out, hidden: open - (limit - budget) };
  }

  const showMore = (hidden: number) => hidden > 0 && (
    <div className="pb-4 text-center">
      <Button variant="outline" size="sm" onClick={() => setLimit((n) => n + PAGE_SIZE)}>Show {Math.min(PAGE_SIZE, hidden)} more ({hidden} left)</Button>
    </div>
  );

  function renderTouches() {
    if (rows.length === 0) return <p className="text-sm text-muted-foreground px-1 py-4 text-center">{searching ? "Nothing matches the search." : stage?.empty}</p>;
    const { out, hidden } = allot(sections.map((sec) => ({ key: sec.opportunity_id, items: sec.items })));
    return (
      <>
        {isNeeds && selectable.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={selected.size > 0 && selected.size === selectable.length}
                onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map((t) => t.id)) : new Set())} />
              Select all with no profile ({selectable.length})
            </label>
            {selected.size > 0 && (
              <form className="flex flex-wrap items-center gap-2 sm:ml-auto" onSubmit={(e) => { e.preventDefault(); void markSelectedNoProfile(); }}>
                <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} maxLength={300} aria-label="Reason for all selected"
                  placeholder="Where you looked, what you found (required)" className={`w-72 px-3 py-1.5 ${FORM_CONTROL}`} />
                <Button type="submit" variant="outline" size="sm" className="border-destructive/50 text-destructive hover:bg-destructive/10" disabled={bulkBusy || !bulkReason.trim()}>
                  {bulkBusy ? "Saving…" : `Mark ${selected.size} as Profile not found`}
                </Button>
              </form>
            )}
          </div>
        )}
        {out.map(({ sec, take, folded }) => {
          const section = sections.find((x) => x.opportunity_id === sec.key)!;
          if (!folded && take.length === 0) return null;
          return (
            <section key={section.opportunity_id} aria-label={section.title} className="mb-2">
              <div className="flex items-center gap-2 pb-2">
                <button type="button" aria-expanded={!folded} onClick={() => toggleSection(section.opportunity_id)} className="inline-flex items-center gap-1 text-sm font-semibold text-foreground">
                  {folded ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {section.title}
                </button>
                <span className="text-xs text-muted-foreground">({section.items.length})</span>
                <Link className="text-xs text-muted-foreground underline underline-offset-2" to={`${crmBase()}?opportunity_id=${section.opportunity_id}`}>open RFP</Link>
              </div>
              {!folded && take.map((t: TouchView) => (
                <div
                  key={t.id}
                  className={cn(
                    "grid transition-[grid-template-rows,opacity,transform] duration-500 ease-in",
                    leaving.has(t.id) ? "grid-rows-[0fr] opacity-0 translate-x-10 pointer-events-none" : "grid-rows-[1fr]",
                  )}
                >
                  <div className="overflow-hidden min-h-0">
                    <div className={cn("pb-4", t.id === arrivedId && "due-arrive-flash")}>
                      <TouchCard touch={t} onChanged={onTouchChanged} compact hideRfp
                        selectable={isNeeds && t.needs_profile && t.actions.includes("no_profile") ? { checked: selected.has(t.id), onToggle: () => toggleSelected(t.id) } : undefined} />
                    </div>
                  </div>
                </div>
              ))}
            </section>
          );
        })}
        {showMore(hidden)}
      </>
    );
  }

  function renderStopped() {
    if (stopped.length === 0) return <p className="text-sm text-muted-foreground px-1 py-4 text-center">{searching ? "Nothing matches the search." : stage?.empty}</p>;
    const causes = (["skipped", "other"] as const).map((c) => ({ key: c, items: stopped.filter((g) => g.cause === c) })).filter((c) => c.items.length > 0);
    // the section keys here are strings, never in `collapsed` (those are RFP ids), so no cause section can fold
    const { out, hidden } = allot(causes);
    return (
      <>
        {out.map(({ sec, take }) => take.length > 0 && (
          <section key={sec.key} aria-label={CAUSE_HEADINGS[sec.key as StoppedGroup["cause"]]} className="mb-2">
            <h3 className="pb-2 text-sm font-semibold text-foreground">
              {CAUSE_HEADINGS[sec.key as StoppedGroup["cause"]]} <span className="text-xs font-normal text-muted-foreground">({sec.items.length})</span>
            </h3>
            {take.map((g: StoppedGroup) => (
              <div key={g.key} className={cn("pb-4", g.touches.some((t) => t.id === arrivedId) && "due-arrive-flash")}>
                <StoppedGroupCard group={g} onChanged={onTouchChanged} />
              </div>
            ))}
          </section>
        ))}
        {showMore(hidden)}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div><LinkedInHubTabs /></div>
      <PageHeader
        title="LinkedIn Outreach"
        description={data && !switching ? `${formatCalendarDate(data.today)} (Karachi) · ${total === 0 ? "nothing due" : `${total} to do`}` : "Loading…"}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search company, person or RFP"
                aria-label="Search the due list" className={`pl-8 pr-3 py-1.5 w-64 ${FORM_CONTROL}`} />
            </div>
          <select value={rfp} onChange={(e) => setRfp(e.target.value ? Number(e.target.value) : "")} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Filter by RFP">
            <option value="">All RFPs</option>
            {Object.entries(rfps).map(([id, title]) => <option key={id} value={id}>{title}</option>)}
          </select>
          </div>
        }
      />
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {loading && <DueSkeleton />}
      {data && !switching && total === 0 && data.groups.waiting_for_start.length === 0 && <p className="text-sm text-muted-foreground">Nothing due today.</p>}
      {data && !switching && <DueStatBoxes data={data} onOpen={(key) => setStageParam(key)} onOpenInbox={() => navigate(conversationsPath("waiting=us"))} />}
      {data && !switching && data.coming_up.length > 0 && (
        <button type="button" onClick={() => setShowUpcoming(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-info-border bg-info-soft px-4 py-2.5 text-left text-sm font-medium text-info-soft-foreground hover:opacity-90 transition-opacity">
          <CalendarClock className="w-4 h-4 shrink-0" aria-hidden />
          Coming up in the next 3 days ({data.coming_up.length})
          <span className="ml-auto text-xs font-normal underline underline-offset-2">View details</span>
        </button>
      )}
      {data && !switching && (
        <DueStageStrip data={view ?? data} selected={openStage} onSelect={(key) => setStageParam(key)} arrival={arrival} />
      )}
      {data && !switching && stage && (
        <section aria-label={stage.title} className="rounded-xl border border-border bg-surface overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface-muted">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground">
              <span>{stage.title} <span className="text-muted-foreground font-normal">({shown})</span></span>
              {isWarm && <StandingHelp />}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{stage.hint}</p>
          </div>
          <div className="p-4 pb-0 bg-surface-muted/60">
            {isWarm
              ? (warmLeads.length === 0
                ? <p className="text-sm text-muted-foreground px-1 py-4 text-center">{searching ? "No warm company matches the search." : stage.empty}</p>
                : <div className="space-y-3 pb-3">{warmLeads.map((l) => <WarmLeadCard key={l.id} lead={l} onChanged={() => { load(); if (closedLeads) loadClosedLeads(); }} />)}</div>)
              : isClosed
              ? renderStopped()
              : renderTouches()}
          </div>
          {isWarm && (
            <div className="border-t border-border px-4 py-3 space-y-3">
              <button type="button" className="text-sm font-medium text-primary underline underline-offset-2" aria-expanded={closedLeads !== null}
                onClick={() => (closedLeads === null ? loadClosedLeads() : setClosedLeads(null))}>
                {closedLeads === null ? "Show skipped and closed leads" : "Hide skipped and closed leads"}
              </button>
              {closedLeads !== null && (closedLeads.length === 0
                ? <p className="text-sm text-muted-foreground">Nothing skipped.</p>
                : <div className="space-y-3">{closedLeads.map((l) => <WarmLeadCard key={l.id} lead={l} closed onChanged={() => { load(); loadClosedLeads(); }} />)}</div>)}
            </div>
          )}
        </section>
      )}
      {showUpcoming && data && <ComingUpModal items={data.coming_up} onClose={() => setShowUpcoming(false)} onChanged={load} />}
    </div>
  );
}
